import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { auth, APIError } from '../api/client'
import { AuthLayout } from '../components/layout/AuthLayout'
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  GoogleIcon,
  GitHubIcon,
  DiscordIcon,
  TwitterIcon,
  TelegramIcon,
} from '../components/ui'

const providerIcons: Record<string, React.FC<{ className?: string }>> = {
  google: GoogleIcon,
  github: GitHubIcon,
  discord: DiscordIcon,
  twitter: TwitterIcon,
  telegram: TelegramIcon,
}

const providerNames: Record<string, string> = {
  google: 'Google',
  github: 'GitHub',
  discord: 'Discord',
  twitter: 'X (Twitter)',
  telegram: 'Telegram',
}

export function LinkAccountPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const token = searchParams.get('token')
  const provider = searchParams.get('provider') || 'unknown'
  const email = searchParams.get('email')

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const ProviderIcon = providerIcons[provider] || GoogleIcon
  const providerName = providerNames[provider] || provider

  const handleLink = async () => {
    if (!token) {
      setError('Invalid linking request')
      return
    }

    setLoading(true)
    setError(null)

    try {
      await auth.confirmLink(token, provider)

      // Redirect to account dashboard
      const accountUrl = import.meta.env.VITE_ACCOUNT_URL || '/'
      window.location.href = `${accountUrl}?linked=${provider}`
    } catch (err) {
      if (err instanceof APIError) {
        setError(err.message)
      } else {
        setError('Failed to link account. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    navigate('/login')
  }

  if (!token) {
    return (
      <AuthLayout>
        <Card>
          <CardHeader>
            <CardTitle>Invalid Request</CardTitle>
            <CardDescription>This linking request is invalid or has expired.</CardDescription>
          </CardHeader>
          <CardContent>
            <Alert variant="error">
              The account linking request is invalid. Please try signing in again.
            </Alert>
          </CardContent>
          <CardFooter>
            <Link
              to="/login"
              className="font-medium text-[var(--text-primary)] hover:opacity-80 transition-opacity"
            >
              Back to sign in
            </Link>
          </CardFooter>
        </Card>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <Card>
        <CardHeader>
          <CardTitle>Link Your Account</CardTitle>
          <CardDescription>
            We found an existing account with this email
          </CardDescription>
        </CardHeader>

        <CardContent>
          {error && (
            <Alert variant="error" className="mb-4">
              {error}
            </Alert>
          )}

          <div className="space-y-4">
            <div className="flex items-center justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[var(--bg-elevated)]">
                <ProviderIcon className="h-8 w-8 text-[var(--text-secondary)]" />
              </div>
            </div>

            <div className="text-center">
              <p className="text-[var(--text-secondary)]">
                An account with the email{' '}
                <span className="font-medium text-[var(--text-primary)]">
                  {email}
                </span>{' '}
                already exists.
              </p>
              <p className="mt-2 text-sm text-[var(--text-secondary)]">
                Would you like to link your {providerName} account to access your existing account?
              </p>
            </div>

            <Alert variant="info">
              After linking, you'll be able to sign in with either your password or {providerName}.
            </Alert>

            <div className="space-y-3">
              <Button
                fullWidth
                loading={loading}
                onClick={handleLink}
              >
                <ProviderIcon className="mr-2 h-5 w-5" />
                Link {providerName} Account
              </Button>

              <Button
                fullWidth
                variant="outline"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </Button>
            </div>
          </div>
        </CardContent>

        <CardFooter>
          <span className="text-sm text-[var(--text-secondary)]">
            Not your account?
          </span>
          <Link
            to="/login"
            className="font-medium text-[var(--text-primary)] hover:opacity-80 transition-opacity"
          >
            Sign in with a different account
          </Link>
        </CardFooter>
      </Card>
    </AuthLayout>
  )
}
